import { FormEventHandler, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { colord } from "colord";
import { Flex } from "@chakra-ui/react";

import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { ConsultantProfile } from "../components/ConsultantProfile";
import { Button } from "../components/button/Button";
import { Back } from "../components/button/Back";
import LoadingIndicator from "../components/Loading";
import { Message } from "../components/Message";
import { PageTitle } from "../components/text/PageTitle";
import { Input } from "../components/form/Input";
import { TextArea } from "../components/form/TextArea";
import { Hr } from "../components/Hr";
import { Calendar } from "../components/Calendar";
import { SummaryTable } from "../components/SummaryTable";

import {
	Check,
	People,
	Speaker,
} from "../components/icons";
import Lightning from "../components/icons/Zap";

import { useCustomer, useLoadMatchingConsultants, useCreateJob } from "@api/hooks";
import { useOrganisation } from "@api/hooks";

import { Job } from "@models/Job";
import { Consultant } from "@models/Consultant";
import { routes } from "../routes";
import { appTheme } from "@styles/Theme";
import { formatShift, bookingType as humanReadableBookingType } from "@utils/utils";

const JobEditPage = () => {
	const navigate = useNavigate();
	const params = useParams();
	const branchId = params.id || "";

	const { organisation } = useOrganisation();
	const branches = organisation?.competences || [];
	const branch = branches?.find((b) => b.key === branchId);
	const services = branch?.items || [];

	const [selectedService, setSelectedService] = useState<string>("");
	const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
	const [showSkills, setShowSkills] = useState(false);
	const [showShifts, setShowShifts] = useState(false);
	const [showDescription, setShowDescription] = useState(false);
	const [showCustomer, setShowCustomer] = useState(false);
	const [showBookingType, setShowBookingType] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);

	const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
	const [selectedShifts, setSelectedShifts] = useState<Job["shifts"]>([]);
	const [description, setDescription] = useState("");
	const [bookingType, setBookingType] = useState<Job["bookingType"]>();
	const [tocAccepted, setTocAccepted] = useState(false);
	const [selectedConsultants, setSelectedConsultants] = useState<Map<string, Consultant>>(new Map());

	const { customer, isLoading: isLoadingCustomer } = useCustomer(selectedCustomerId);
	const { mutate: createJob, isLoading: isCreating, isSuccess, error: errorCreatingJob } = useCreateJob();
	const {
		data: matchingConsultants,
		mutate: loadMatchingConsultants,
		isLoading: loadingMatches,
	} = useLoadMatchingConsultants();

	const service = services?.find((s) => s.key === selectedService);
	const skills = service?.items || [];
	const customerDescription = customer?.description;

	useEffect(() => {
		if (!branch) {
			navigate(routes.jobCreate);
		}
	}, [branch, navigate]);

	const scrollTo = (id: string) => {
		setTimeout(() => {
			const el = document.getElementById(id);

			if (el) {
				el.scrollIntoView({ block: "center", behavior: "smooth" });
			}
		}, 100);
	};

	const handleServiceSelection = (serviceKey: string) => {
		setSelectedService(serviceKey);
		setShowSkills(true);
		scrollTo("skills");
	};

	const toggleSelectSkill = (skillKey: string) => {
		setSelectedSkills((prevSkills) => {
			const hasSkill = prevSkills.includes(skillKey);

			if (hasSkill) return prevSkills.filter((s) => s !== skillKey);
			return [...prevSkills, skillKey];
		});
	};

	const toggleSelectConsultant = (consultant: Consultant) => {
		setSelectedConsultants((prev) => {
			const newMap = new Map(prev);

			if (newMap.has(consultant.id)) {
				newMap.delete(consultant.id);
			} else {
				newMap.set(consultant.id, consultant);
			}

			return newMap;
		});
	};

	const handleSubmit: FormEventHandler = (e) => {
		e?.preventDefault();

		if (!selectedCustomerId || !description || !bookingType) return;

		createJob(
			{
				competence: {
					branch: branchId,
					service: selectedService,
					skills: selectedSkills,
				},
				description,
				customerId: selectedCustomerId,
				shifts: selectedShifts,
				bookingType,
				...(bookingType === "INVITATION" && { invitedConsultantIds: Array.from(selectedConsultants.keys()) }),
			},
			{
				onSuccess: (newJob) => {
					setTimeout(() => {
						navigate(routes.job(newJob.id));
					}, 3000);
				},
			},
		);
	};

	if (!branch) return null;

	return (
		<LoggedInLayout width="s">
			<Styles className={isSuccess ? "sent" : ""}>
				<PageTitle>Skapa uppdrag</PageTitle>
				<Back />

				<div className="ctn">
					<div>
						<p>
							<strong>Vilken tjänst efterfrågas?</strong>
						</p>
						<p className="small">Klicka på den tjänst som passar bäst:</p>
					</div>

					<div className="choices">
						<div className="missing-branch">{branch.name.sv}</div>

						{services?.map((s) => (
							<div
								key={s.key}
								className={`choice ${selectedService === s.key ? "active-choice" : ""}`}
								onClick={() => handleServiceSelection(s.key)}
							>
								{s.name.sv}
								{selectedService === s.key && <Check color="white" />}
							</div>
						))}
					</div>

					{showSkills && (
						<>
							<Hr id="skills" />

							<div className="skills">
								<p>
									<strong>Vilka arbetsuppgifter ingår?</strong>
								</p>
								<p className="small">Välj en eller flera uppgifter som passar bäst (valfritt):</p>

								<div className="checkbox-choices">
									{skills.map((s) => (
										<div
											key={s.key}
											className={`checkbox-choice ${selectedSkills.includes(s.key) ? "active-choice" : ""}`}
											onClick={() => toggleSelectSkill(s.key)}
										>
											{s.name.sv}
										</div>
									))}
								</div>
							</div>

							<Button
								type="success"
								title="Fortsätt"
								className="next-btn"
								onClick={() => {
									setShowShifts(true);
									scrollTo("shifts");
								}}
							/>
						</>
					)}

					{showShifts && (
						<>
							<Hr id="shifts" />

							<div>
								<p>
									<strong>När ska uppdraget utföras?</strong>
								</p>
							</div>

							<Calendar shifts={selectedShifts} setShifts={setSelectedShifts} />

							<Button
								type="success"
								title="Fortsätt"
								className="next-btn"
								disabled={!selectedShifts.length}
								onClick={() => {
									setShowDescription(true);
									scrollTo("description");
								}}
							/>
						</>
					)}

					{showDescription && (
						<>
							<Hr id="description" />

							<div>
								<p>
									<strong>Uppdragsbeskrivning:</strong>
								</p>
							</div>

							<TextArea
								className="description-textarea"
								label="Beskriv uppdraget"
								onChange={(val) => setDescription(val)}
								value={description}
								placeholder="T.ex: Du ska ansvara för barn i åldern 3-5 år i Pingvingruppen. Du kommer jobba tillsammans med Åsa. Vi önskar en konsult med kunskap i reggio emilia."
							/>

							<Button
								type="success"
								title="Fortsätt"
								className="next-btn"
								disabled={!description}
								onClick={() => {
									setShowCustomer(true);
									scrollTo("customer");
								}}
							/>
						</>
					)}

					{showCustomer && (
						<>
							<Hr id="customer" />

							<div>
								<p>
									<strong>Vilken enhet/skola gäller uppdraget för?</strong>
								</p>
							</div>

							{isLoadingCustomer ? (
								<LoadingIndicator />
							) : (
								<>
									<div className="choices">
										{customer?.childCustomers?.map((c) => (
											<div
												key={c.id}
												className={`choice ${selectedCustomerId === c.id ? "active-choice" : ""}`}
												onClick={() => setSelectedCustomerId(c.id)}
											>
												{c.name}
												{selectedCustomerId === c.id && <Check color="white" />}
											</div>
										))}
									</div>

									<Button
										type="success"
										title="Fortsätt"
										className="next-btn"
										disabled={!selectedCustomerId}
										onClick={() => {
											setShowBookingType(true);
											scrollTo("bookingType");
										}}
									/>
								</>
							)}
						</>
					)}

					{isLoadingCustomer && <LoadingIndicator />}

					{customerDescription && (
						<>
							<Hr />

							<div>
								<p>
									<strong>Om arbetsplatsen:</strong>
								</p>
								<p>{customerDescription}</p>
							</div>

							<Button
								type="success"
								title="Fortsätt"
								className="next-btn"
								disabled={!description}
								onClick={() => {
									setShowBookingType(true);
									scrollTo("bookingType");
								}}
							/>
						</>
					)}

					{showBookingType && (
						<>
							<Hr id="bookingType" />

							<div>
								<p>
									<strong>Hur ska uppdraget bokas och tillsättas?</strong>
								</p>
								<p>Klicka på det alternativ som passar er bäst:</p>
							</div>

							<Flex className="booking-type" gap="2rem" direction={{ base: "column", md: "row" }}>
								<div
									className={bookingType === "OPEN" ? "selected" : ""}
									onClick={() => {
										setBookingType("OPEN");
										setShowConfirm(true);
										scrollTo("confirm");
									}}
								>
									<Speaker />

									<h3>En matchande konsult bokas in</h3>
									<p className="small">
										Uppdraget publiceras för matchande konsulter. Konsulter anmäler intresse och du väljer vem du vill
										anlita.
									</p>
								</div>
								<div
									className={bookingType === "AUTO" ? "selected" : ""}
									onClick={() => {
										setBookingType("AUTO");
										setShowConfirm(true);
										scrollTo("confirm");
									}}
								>
									<Lightning />
									<h3>Automatisk matchning och bokning</h3>
									<p className="small">
										Publiceras till alla matchande konsulter. Den första konsult som ansöker om jobbet kommer bli bokad.
									</p>
								</div>
								<div
									className={bookingType === "INVITATION" ? "selected" : ""}
									onClick={() => {
										scrollTo("invite");
										setBookingType("INVITATION");
										loadMatchingConsultants();
									}}
								>
									<People />
									<h3>Önska konsulter</h3>
									<p className="small">
										Önska matchande konsulter som kan autoboka sig själva. Uppdraget publiceras också för övriga
										matchande konsulter som anmäler intresse som du kan boka in.
									</p>
								</div>
							</Flex>
						</>
					)}

					{bookingType === "INVITATION" && (
						<>
							<Hr id="invite" />

							<div>
								<p>
									<strong>Välj den/de konsulter du vill bjuda in:</strong>
								</p>
							</div>

							{loadingMatches ? (
								<LoadingIndicator text="Hittar matchande konsulter..." />
							) : (
								<>
									<div className="matching-consultants">
										{matchingConsultants?.users?.map((m) => {
											const isSelected = selectedConsultants.has(m.id);

											return (
												<div key={m.id} className={isSelected ? "selected-consultant" : ""}>
													<ConsultantProfile consultant={m} size="s" openProfileInNewTab={true} />

													<div className="consultant-btns">
														<Button
															size="s"
															type="outline-success"
															onClick={() => toggleSelectConsultant(m)}
															title={isSelected ? "Ångra val" : "Välj konsult"}
														/>
														<Button size="s" type="outline" onClick={() => {}} title="Visa mer" />
													</div>
													{isSelected && (
														<div className="check">
															<Check color={appTheme.colors.success} />
														</div>
													)}
												</div>
											);
										})}
									</div>
									<Button
										type="success"
										title={
											selectedConsultants.size
												? `Bekräfta ${selectedConsultants.size} konsult(er)`
												: "Välj minst en konsult"
										}
										className="next-btn"
										disabled={!selectedConsultants.size}
										onClick={() => {
											setShowConfirm(true);
											scrollTo("confirm");
										}}
									/>
								</>
							)}
						</>
					)}
					{showConfirm && (
						<>
							<Hr id="confirm" />

							<div>
								<p>
									<strong>Granska uppgifterna och skicka beställning</strong>
								</p>
								<p>Skrolla upp för att göra eventuella korrigeringar.</p>
							</div>

							<SummaryTable
								data={[
									{
										label: "Enhet/skola",
										text: customer?.name || "",
									},
									{
										label: "Efterfrågad kompetens",
										text: `${service?.name.sv || "[Tjänst saknas]"}\n${selectedSkills.join(", ")}`,
									},
									{
										label: "Arbetspass",
										text: selectedShifts.map((s) => formatShift(s.start, s.end)),
									},
									{
										label: "Uppdragsbeskrivning",
										text: description,
									},
									{
										label: "Om arbetsplatsen",
										text: customerDescription || "",
									},
									{
										label: "Bokningstyp",
										text: [
											bookingType ? humanReadableBookingType(bookingType) : "",
											...Array.from(selectedConsultants.values()).map((c) => "- " + c.firstName + " " + c.lastName),
										],
									},
								]}
							/>

							<div className="toc">
								<input type="checkbox" checked={tocAccepted} onChange={(e) => setTocAccepted(e.target.checked)} />
								<p className="small">
									Härmed accepterar jag bokningsvillkoren (tryck{" "}
									<Link to={routes.toc} target="_blank" rel="noopener noreferrer">
										här
									</Link>{" "}
									för att öppna i nytt fönster)
								</p>
							</div>

							<Button
								type="success"
								className="next-btn"
								title="Beställ uppdraget"
								disabled={!tocAccepted}
								onClick={handleSubmit}
								loadingTitle="Skickar..."
							/>
						</>
					)}

					{isSuccess && (
						<>
							<Hr id="success" />
							<div className="success">
								<div className="icon-ctn">
									<Check color={appTheme.colors.success} size={32} />
								</div>
								<h3>Uppdraget har skickats</h3>
								<p>Tack för din beställning! Håll utkik efter uppdateringar i din app eller här i kundportalen.</p>
							</div>
						</>
					)}
				</div>
			</Styles>
		</LoggedInLayout>
	);
};

export default JobEditPage;

const Styles = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;

	.ctn {
		display: flex;
		flex-direction: column;
		gap: 3rem;
		padding-bottom: 75vh;
	}

	p:last-child {
		margin-bottom: 0;
	}

	p:first-child {
		margin-top: 0;
	}

	.sent {
		> *:not(.success) {
			opacity: 0.45;
			pointer-events: none;
		}
	}

	.choice {
		border: 2px solid ${(props) => props.theme.colors.brand};
		background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.5).toHex()};
		text-align: center;
		padding: 0.75rem 1rem;
		border-radius: 25px;
		font-weight: 500;
		position: relative;
		cursor: pointer;
		width: 50vw;
		max-width: 650px;
		width: 100%;
		margin: 0 1rem;

		transition: transform 0.125s;

		&:hover {
			background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.45).toHex()};
			transform: scale(0.99);
		}
		svg {
			position: absolute;
			right: 1rem;
			top: 0.9rem;
		}

		&.active-choice {
			background-color: ${(props) => props.theme.colors.brand};
			color: white;
		}
	}

	.missing-branch {
		color: ${(props) => props.theme.colors.darkRed};
		font-size: 1.1rem;
	}

	.choices {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		min-height: 250px;
	}

	.checkbox-choices {
		display: inline-flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		align-items: flex-start;
	}

	.checkbox-choice {
		border: 2px solid ${(props) => props.theme.colors.brand};
		background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.5).toHex()};
		text-align: center;
		padding: 0.75rem;
		border-radius: 25px;
		font-weight: 500;
		position: relative;
		cursor: pointer;

		transition: transform 0.125s;

		&:hover {
			background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.45).toHex()};
			transform: scale(0.99);
		}

		&.active-choice {
			background-color: ${(props) => props.theme.colors.brand};
			color: white;
		}
	}

	.skills {
		margin-bottom: 1.5rem;
	}

	.booking-type {
		> div {
			display: flex;
			flex-direction: column;
			flex: 1;
			border: 3px solid ${(props) => props.theme.colors.dividerLight};
			padding: 1.5rem;
			border-radius: 10px;
			text-align: center;
			background-color: #f9f9f9;
			cursor: pointer;
			align-items: center;
			gap: 0.75rem;

			h3 {
				line-height: 1.2;
			}

			&.selected {
				border-color: ${(props) => props.theme.colors.brand};
				background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.48).toHex()};
			}

			&:hover:not(.selected) {
				border-color: ${(props) => colord(props.theme.colors.dividerLight).darken(0.1).toHex()};
			}

			/* svg {
        margin: 0 0 1rem 0;
      } */

			h3 {
				font-weight: 500;
				font-size: 1rem;
			}
		}
	}

	.next-btn {
		max-width: 310px;
		margin: 0 auto;
	}

	.matching-consultants {
		display: flex;
		flex-wrap: wrap;

		> div {
			/* flex: 1; */
			width: 46%;
			margin: 0.5rem 2%;
			padding: 1rem 1rem 0.5rem 1rem;
			position: relative;
		}

		.check {
			position: absolute;
			top: 1.5rem;
			right: 1.5rem;
		}

		.consultant-btns {
			padding-top: 1rem;
			display: flex;
			gap: 0.75rem;
			justify-content: flex-end;

			button {
				background-color: #fff;
			}
		}

		.selected-consultant {
			background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.48).toHex()};
		}
	}

	.contact-person {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.toc {
		/* margin: 0 auto; */
		/* width: 600px; */
		display: flex;
		border: 1px solid ${(props) => props.theme.colors.divider};
		border-radius: 5px;
		padding: 1rem;
		gap: 1rem;
		/* justify-content: center; */
		align-items: center;

		p {
			margin: 0;
		}

		input {
			width: 1rem;
			height: 1rem;
		}
	}

	.success {
		border: 1px solid ${(props) => props.theme.colors.dividerLight};
		border-radius: 5px;
		padding: 2.75rem 2rem 2.5rem 2rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		gap: 1.5rem;

		> * {
			margin: 0;
		}
	}

	.file-upload {
		margin-top: 0.75rem;
	}
`;
